import React, { useEffect, useState } from "react";
import { api, brl, formatDateTime } from "../../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Barcode, UploadSimple, ArrowsClockwise, Package, Tag, Gear, ClockCounterClockwise, ListNumbers } from "@phosphor-icons/react";
import BRLInput from "../../components/BRLInput";

const TABS = [
  { key: "products", label: "Produtos", icon: Package },
  { key: "categories", label: "Categorias", icon: Tag },
  { key: "mobmap", label: "Mapeamento MobVender", icon: ListNumbers },
  { key: "import", label: "Importar CSV/Excel", icon: UploadSimple },
  { key: "movements", label: "Movimentações", icon: ClockCounterClockwise },
  { key: "settings", label: "Configurações", icon: Gear },
];

const EMPTY_PRODUCT = { categoria: "", codigo_barras: "", nome_produto: "", custo: 0, valor_venda: 0, quantidade_estoque: 0, status: "active" };

export default function AdminProducts() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [prodModal, setProdModal] = useState(null); // product being edited/created
  const [adjModal, setAdjModal] = useState(null);    // { product, quantidade, reason }
  const [newCat, setNewCat] = useState("");
  const [movements, setMovements] = useState([]);
  const [settings, setSettings] = useState({ block_on_insufficient: false, price_change_policy: "all" });

  // Import CSV/Excel
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseData, setParseData] = useState(null);   // { items, errors, grupos, categorias, summary, source, filename }
  const [importItems, setImportItems] = useState([]);
  const [categoryMap, setCategoryMap] = useState({}); // grupo -> nome categoria
  const [confirming, setConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [missingSel, setMissingSel] = useState(new Set());
  const [missingAction, setMissingAction] = useState("manter");
  const [applyingMissing, setApplyingMissing] = useState(false);
  const [savingMap, setSavingMap] = useState(false);
  // Mapeamento MobVender (tabela permanente)
  const [mobMap, setMobMap] = useState([]);
  const [newMapCode, setNewMapCode] = useState("");
  const [newMapCat, setNewMapCat] = useState("");
  const loadMobMap = async () => setMobMap((await api.get("/products/category-map")).data.items || []);

  const loadHistory = async () => setHistory((await api.get("/products/import/history")).data || []);

  const loadProducts = async () => {
    const { data } = await api.get("/products", { params: { q, categoria: catFilter, status: statusFilter } });
    setProducts(data || []);
  };
  const loadCategories = async () => setCategories((await api.get("/categories")).data || []);
  const loadMovements = async () => setMovements((await api.get("/stock/movements")).data || []);
  const loadSettings = async () => setSettings((await api.get("/stock/settings")).data || {});

  useEffect(() => { loadCategories(); loadSettings(); }, []);
  useEffect(() => {
    const t = setTimeout(loadProducts, 200);
    return () => clearTimeout(t);
  }, [q, catFilter, statusFilter]);
  useEffect(() => { if (tab === "movements") loadMovements(); if (tab === "import") loadHistory(); if (tab === "mobmap") loadMobMap(); }, [tab]);

  // ---- Product CRUD ----
  const saveProduct = async () => {
    const p = prodModal;
    if (!p.nome_produto?.trim()) { toast.error("Informe o nome do produto"); return; }
    try {
      if (p.id) {
        await api.put(`/products/${p.id}`, {
          categoria: p.categoria, codigo_barras: p.codigo_barras, nome_produto: p.nome_produto,
          custo: Number(p.custo) || 0, valor_venda: Number(p.valor_venda) || 0,
          quantidade_estoque: Number(p.quantidade_estoque) || 0, status: p.status,
        });
      } else {
        await api.post("/products", {
          categoria: p.categoria, codigo_barras: p.codigo_barras, nome_produto: p.nome_produto,
          custo: Number(p.custo) || 0, valor_venda: Number(p.valor_venda) || 0,
          quantidade_estoque: Number(p.quantidade_estoque) || 0, status: p.status,
        });
      }
      toast.success("Produto salvo");
      setProdModal(null);
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar produto"); }
  };

  const deactivateProduct = async (p) => {
    if (!window.confirm(`Desativar "${p.nome_produto}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Produto desativado"); loadProducts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const saveAdjust = async () => {
    try {
      await api.post(`/products/${adjModal.product.id}/stock-adjust`, {
        quantidade_estoque: Number(adjModal.quantidade) || 0, reason: adjModal.reason || "Ajuste manual",
      });
      toast.success("Estoque ajustado");
      setAdjModal(null);
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ---- Categories ----
  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/categories", { name: newCat.trim(), active: true }); setNewCat(""); loadCategories(); toast.success("Categoria criada"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const toggleCategory = async (c) => {
    try { await api.put(`/categories/${c.id}`, { name: c.name, active: !c.active }); loadCategories(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ---- Settings ----
  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await api.put("/stock/settings", next); toast.success("Configuração salva"); }
    catch (e) { toast.error("Erro ao salvar configuração"); }
  };

  // ---- Import CSV/Excel ----
  const doParse = async () => {
    if (!file) { toast.error("Selecione um arquivo CSV ou Excel"); return; }
    setParsing(true); setParseData(null); setImportItems([]); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParseData(data);
      setImportItems(data.items || []);
      // pré-preenche o mapa de categorias a partir da tabela permanente MobVender (vazio = não cadastrado)
      const cm = {};
      (data.grupos || []).forEach((g) => { cm[g] = (data.saved_map && data.saved_map[g]) || ""; });
      setCategoryMap(cm);
      setMissingSel(new Set((data.missing || []).map((m) => m.id)));
      setMissingAction("manter");
      const s = data.summary || {};
      toast.success(`${s.total || 0} linhas lidas · ${s.criar || 0} novos, ${s.atualizar || 0} atualizações`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao ler o arquivo"); }
    finally { setParsing(false); }
  };

  const setImportField = (i, k, v) => { const a = [...importItems]; a[i] = { ...a[i], [k]: v }; setImportItems(a); };
  const removeImportItem = (i) => setImportItems(importItems.filter((_, k) => k !== i));
  const setGroupCat = (g, v) => setCategoryMap((m) => ({ ...m, [g]: v }));

  const saveGroupMap = async () => {
    const entries = Object.entries(categoryMap).filter(([, v]) => (v || "").trim());
    if (!entries.length) { toast.error("Preencha ao menos uma categoria"); return; }
    setSavingMap(true);
    try {
      for (const [g, v] of entries) { await api.put(`/products/category-map/${g}`, { categoria: v.trim() }); }
      toast.success("Mapeamento salvo. Estes códigos não serão mais perguntados.");
      loadCategories();
      if (parseData) setParseData((pd) => ({ ...pd, saved_map: { ...(pd.saved_map || {}), ...Object.fromEntries(entries.map(([g, v]) => [g, v.trim()])) }, unmapped_grupos: (pd.unmapped_grupos || []).filter((g) => !categoryMap[g]?.trim()) }));
    } catch (e) { toast.error("Erro ao salvar mapeamento"); }
    finally { setSavingMap(false); }
  };

  // ---- Mapeamento MobVender (tabela permanente) ----
  const addMapEntry = async () => {
    if (!newMapCode.trim() || !newMapCat.trim()) { toast.error("Informe o código e a categoria"); return; }
    try {
      await api.put(`/products/category-map/${newMapCode.trim()}`, { categoria: newMapCat.trim() });
      setNewMapCode(""); setNewMapCat(""); loadMobMap(); loadCategories();
      toast.success("Mapeamento adicionado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const saveMapEntry = async (codigo, categoria) => {
    try { await api.put(`/products/category-map/${codigo}`, { categoria }); loadMobMap(); loadCategories(); toast.success("Salvo"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const removeMapEntry = async (codigo) => {
    if (!window.confirm(`Remover o mapeamento do código ${codigo}?`)) return;
    try { await api.delete(`/products/category-map/${codigo}`); loadMobMap(); toast.success("Removido"); }
    catch (e) { toast.error("Erro"); }
  };

  const toggleMissing = (id) => setMissingSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllMissing = () => setMissingSel((s) => {
    const all = (parseData?.missing || []).map((m) => m.id);
    return s.size === all.length ? new Set() : new Set(all);
  });
  const applyMissingAction = async () => {
    if (missingAction === "manter" || missingAction === "ignorar") { toast("Nenhuma alteração aplicada (manter/ignorar)"); return; }
    const ids = [...missingSel];
    if (!ids.length) { toast.error("Selecione ao menos um produto"); return; }
    setApplyingMissing(true);
    try {
      const { data } = await api.post("/products/bulk-action", { ids, action: missingAction });
      toast.success(`${data.affected} produto(s) atualizados`);
      setParseData((pd) => pd ? { ...pd, missing: (pd.missing || []).filter((m) => !missingSel.has(m.id)), missing_count: (pd.missing_count || 0) - ids.length } : pd);
      setMissingSel(new Set());
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setApplyingMissing(false); }
  };

  const doConfirmImport = async () => {
    const valid = importItems.filter((it) => (it.nome_produto || "").trim() && it.action !== "ignorar");
    if (valid.length === 0) { toast.error("Nada para importar"); return; }
    setConfirming(true);
    try {
      const { data } = await api.post("/products/import/confirm", {
        filename: parseData?.filename || "", source: parseData?.source || "csv",
        category_map: categoryMap,
        items: valid.map((it) => ({
          codigo_barras: it.codigo_barras || "", nome_produto: it.nome_produto, grupo: it.grupo || "",
          categoria: it.categoria || "", custo: Number(it.custo) || 0, valor_venda: Number(it.valor_venda) || 0,
          quantidade_estoque: Number(it.quantidade_estoque) || 0, action: it.action,
        })),
      });
      setImportResult(data);
      toast.success(`Importação concluída: ${data.created} criados, ${data.updated} atualizados`);
      setParseData(null); setImportItems([]); setFile(null); setCategoryMap({});
      loadProducts(); loadCategories(); loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar importação"); }
    finally { setConfirming(false); }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Catálogo</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Produtos & Estoque</h1>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#EBE5DF] mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-xs tracking-widest uppercase font-medium flex items-center gap-2 whitespace-nowrap border-b-2 ${tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#5C5A57] hover:text-[#0A0A0A]"}`}
              data-testid={`products-tab-${t.key}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* PRODUCTS */}
      {tab === "products" && (
        <div>
          <div className="flex flex-wrap gap-3 mb-4 items-end">
            <input className="input-underline flex-1 min-w-[220px]" placeholder="Buscar por código interno, nome ou código de barras" value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" />
            <select className="input-underline" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="product-cat-filter">
              <option value="">Todas categorias</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select className="input-underline" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="product-status-filter">
              <option value="">Todos status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
            <button onClick={() => setProdModal({ ...EMPTY_PRODUCT })} className="brand flex items-center gap-1" data-testid="new-product-btn"><Plus size={14} /> Novo produto</button>
          </div>

          <div className="border border-[#EBE5DF] bg-white overflow-x-auto">
            <table className="crl w-full">
              <thead><tr><th>Cód. interno</th><th>Produto</th><th>Código de barras</th><th>Categoria</th><th>Custo</th><th>Venda</th><th>Estoque</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {products.length === 0 && <tr><td colSpan={9} className="text-center text-[#5C5A57] py-6" data-testid="products-empty">Nenhum produto.</td></tr>}
                {products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`}>
                    <td className="tabular font-medium text-[#8B5E5E]" data-testid={`product-code-${p.id}`}>{p.codigo_interno || "-"}</td>
                    <td className="font-medium">{p.nome_produto}</td>
                    <td className="text-xs text-[#5C5A57]">{p.codigo_barras || <span className="italic">sem código</span>}</td>
                    <td className="text-xs">{p.categoria || "-"}</td>
                    <td className="tabular">{brl(p.custo)}</td>
                    <td className="tabular font-medium">{brl(p.valor_venda)}</td>
                    <td className={`tabular font-medium ${Number(p.quantidade_estoque) <= 0 ? "text-[#B4564F]" : ""}`} data-testid={`product-stock-${p.id}`}>{p.quantidade_estoque}</td>
                    <td><span className={`text-[10px] tracking-widest uppercase ${p.status === "active" ? "text-[#3F6B45]" : "text-[#8B5E5E]"}`}>{p.status === "active" ? "Ativo" : "Inativo"}</span></td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setAdjModal({ product: p, quantidade: p.quantidade_estoque, reason: "" })} className="text-[#3B6E5E] hover:underline" title="Ajustar estoque" data-testid={`adjust-stock-${p.id}`}><ArrowsClockwise size={16} /></button>
                        <button onClick={() => setProdModal({ ...p })} className="hover:underline" title="Editar" data-testid={`edit-product-${p.id}`}><PencilSimple size={16} /></button>
                        {p.status === "active" && <button onClick={() => deactivateProduct(p)} className="text-[#8B5E5E] hover:underline" title="Desativar" data-testid={`del-product-${p.id}`}><Trash size={16} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CATEGORIES */}
      {tab === "categories" && (
        <div className="max-w-lg">
          <div className="flex gap-2 mb-4">
            <input className="input-underline flex-1" placeholder="Nova categoria" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-input" />
            <button onClick={addCategory} className="brand" data-testid="add-category-btn"><Plus size={14} className="inline mr-1" />Adicionar</button>
          </div>
          <div className="border border-[#EBE5DF] bg-white divide-y divide-[#F0EBE5]">
            {categories.length === 0 && <div className="p-4 text-sm text-[#5C5A57]">Nenhuma categoria.</div>}
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3" data-testid={`category-row-${c.id}`}>
                <span className="text-sm">{c.name}</span>
                <button onClick={() => toggleCategory(c)} className={`text-[10px] tracking-widest uppercase ${c.active ? "text-[#3F6B45]" : "text-[#8B5E5E]"}`} data-testid={`toggle-category-${c.id}`}>{c.active ? "Ativa" : "Inativa"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MOBVENDER CATEGORY MAP */}
      {tab === "mobmap" && (
        <div className="max-w-2xl" data-testid="mobmap-tab">
          <div className="border border-[#EBE5DF] bg-white p-6 mb-6">
            <h2 className="section-title mb-2">Mapeamento de Categorias MobVender</h2>
            <p className="text-sm text-[#5C5A57] mb-4">Relacione cada código de grupo do MobVender (<span className="font-mono">id_b_grupo_produto</span>) a uma categoria do sistema. Na importação, a categoria é reconhecida automaticamente. Códigos novos podem ser cadastrados aqui ou na tela de importação.</p>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Código</label>
                <input className="input-underline w-24 tabular" value={newMapCode} onChange={(e) => setNewMapCode(e.target.value)} placeholder="Ex: 21" data-testid="mobmap-new-code" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Categoria</label>
                <input list="cat-options-map" className="input-underline w-full" value={newMapCat} onChange={(e) => setNewMapCat(e.target.value)} placeholder="Nome da categoria" data-testid="mobmap-new-cat" />
              </div>
              <button onClick={addMapEntry} className="brand" data-testid="mobmap-add-btn"><Plus size={14} className="inline mr-1" />Adicionar</button>
            </div>
            <datalist id="cat-options-map">{categories.map((c) => <option key={c.id} value={c.name} />)}</datalist>
          </div>

          <div className="border border-[#EBE5DF] bg-white overflow-x-auto">
            <table className="crl w-full">
              <thead><tr><th>Código</th><th>Categoria</th><th></th></tr></thead>
              <tbody>
                {mobMap.length === 0 && <tr><td colSpan={3} className="text-center text-[#5C5A57] py-6" data-testid="mobmap-empty">Nenhum mapeamento.</td></tr>}
                {mobMap.map((e) => (
                  <tr key={e.codigo} data-testid={`mobmap-row-${e.codigo}`}>
                    <td className="tabular font-medium text-[#8B5E5E] w-24">{e.codigo}</td>
                    <td>
                      <input list="cat-options-map" defaultValue={e.categoria} className="input-underline w-full"
                        onBlur={(ev) => { const v = ev.target.value.trim(); if (v && v !== e.categoria) saveMapEntry(e.codigo, v); }}
                        data-testid={`mobmap-cat-${e.codigo}`} />
                    </td>
                    <td className="w-12"><button onClick={() => removeMapEntry(e.codigo)} className="text-[#8B5E5E] hover:underline" data-testid={`mobmap-del-${e.codigo}`}><Trash size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* IMPORT CSV/EXCEL */}
      {tab === "import" && (
        <div>
          <div className="border border-[#EBE5DF] bg-white p-6 mb-6">
            <h2 className="section-title mb-3">Importar estoque via CSV / Excel</h2>
            <p className="text-sm text-[#5C5A57] mb-4">Envie o arquivo exportado do MobVender (.csv separado por “;” ou .xlsx). Detectamos a codificação automaticamente. Nada é gravado sem sua confirmação. Produtos existentes são atualizados pelo código de barras; novos são criados.</p>
            <div className="flex flex-wrap gap-3 items-center">
              <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              <button onClick={doParse} disabled={parsing || !file} className="brand" data-testid="import-parse-btn">{parsing ? "Lendo arquivo..." : "Ler e pré-visualizar"}</button>
            </div>
          </div>

          {importResult && (
            <div className="border border-[#B7D8BF] bg-[#EAF3EC] p-4 mb-6 text-sm text-[#2E5A38]" data-testid="import-result">
              Importação concluída: <strong>{importResult.created}</strong> criados · <strong>{importResult.updated}</strong> atualizados · <strong>{importResult.ignorados}</strong> ignorados · <strong>{importResult.com_erro}</strong> com erro.
            </div>
          )}

          {parseData && (
            <div className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="import-preview">
              {/* Resumo */}
              <div className="flex flex-wrap items-center gap-4 mb-5">
                <h2 className="section-title flex-1">Pré-visualização</h2>
                <div className="flex gap-4 text-xs">
                  <span className="text-[#3F6B45]" data-testid="sum-criar">Novos: <strong>{parseData.summary?.criar || 0}</strong></span>
                  <span className="text-[#2D6CB4]" data-testid="sum-atualizar">Atualizar: <strong>{parseData.summary?.atualizar || 0}</strong></span>
                  <span className="text-[#8B7A2E]" data-testid="sum-ignorados">Ignorados: <strong>{parseData.summary?.ignorados || 0}</strong></span>
                  <span className="text-[#B4564F]" data-testid="sum-erro">Com erro: <strong>{parseData.summary?.com_erro || 0}</strong></span>
                </div>
                <button onClick={doConfirmImport} disabled={confirming} className="brand" data-testid="import-confirm-btn">{confirming ? "Importando..." : `Confirmar e importar (${importItems.filter((it) => it.action !== "ignorar" && (it.nome_produto || "").trim()).length})`}</button>
              </div>

              {/* Mapeamento de categorias (id_b_grupo_produto) */}
              {(parseData.grupos || []).length > 0 && (
                <div className="mb-5 p-4 bg-[#F5F0EB] border border-[#EBE5DF]" data-testid="category-map">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Mapeamento de categorias (código do grupo → categoria)</div>
                    <button onClick={saveGroupMap} disabled={savingMap} className="text-xs tracking-widest uppercase font-medium text-[#3F6B45] hover:underline" data-testid="save-group-map-btn">{savingMap ? "Salvando..." : "Salvar mapeamentos"}</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {parseData.grupos.map((g) => {
                      const unmapped = (parseData.unmapped_grupos || []).includes(g) && !(categoryMap[g] || "").trim();
                      return (
                        <div key={g} data-testid={`group-map-${g}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#5C5A57] w-20">Grupo {g}</span>
                            <input list="cat-options" className={`input-underline flex-1 ${unmapped ? "border-[#B4564F]" : ""}`} value={categoryMap[g] || ""} onChange={(e) => setGroupCat(g, e.target.value)} placeholder="Categoria (existente ou nova)" data-testid={`group-cat-input-${g}`} />
                          </div>
                          {unmapped && <div className="text-[11px] text-[#B4564F] ml-[88px] mt-0.5" data-testid={`group-unmapped-${g}`}>Categoria não cadastrada (código {g}) — informe e clique em “Salvar mapeamentos”.</div>}
                        </div>
                      );
                    })}
                  </div>
                  <datalist id="cat-options">
                    {(parseData.categorias || []).map((c) => <option key={c} value={c} />)}
                  </datalist>
                  <p className="text-[11px] text-[#5C5A57] mt-2">Salve os mapeamentos para não serem perguntados novamente. Edite tudo na aba “Mapeamento MobVender”.</p>
                </div>
              )}

              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="crl w-full">
                  <thead><tr><th>Ação</th><th>Cód. interno</th><th>Código de barras</th><th>Nome</th><th>Categoria (override)</th><th>Custo</th><th>Venda</th><th>Estoque</th><th></th></tr></thead>
                  <tbody>
                    {importItems.slice(0, 500).map((it, i) => (
                      <tr key={i} className={it.action === "ignorar" ? "bg-[#FBF3E6] opacity-60" : (it.needs_review ? "bg-[#FBF9F0]" : "")} data-testid={`import-item-${i}`}>
                        <td><span className={`text-[10px] tracking-widest uppercase ${it.action === "atualizar" ? "text-[#2D6CB4]" : it.action === "ignorar" ? "text-[#8B7A2E]" : "text-[#3F6B45]"}`}>{it.action}</span></td>
                        <td className="tabular text-xs text-[#8B5E5E]">{it.codigo_interno || (it.action === "criar" ? "novo" : "-")}</td>
                        <td><input className="input-underline w-32" value={it.codigo_barras || ""} onChange={(e) => setImportField(i, "codigo_barras", e.target.value)} data-testid={`import-barcode-${i}`} /></td>
                        <td><input className="input-underline w-48" value={it.nome_produto || ""} onChange={(e) => setImportField(i, "nome_produto", e.target.value)} data-testid={`import-name-${i}`} /></td>
                        <td><input className="input-underline w-32" value={it.categoria || ""} onChange={(e) => setImportField(i, "categoria", e.target.value)} placeholder={categoryMap[it.grupo] || ""} data-testid={`import-cat-${i}`} /></td>
                        <td><input className="input-underline w-20 tabular" type="number" value={it.custo || 0} onChange={(e) => setImportField(i, "custo", e.target.value)} /></td>
                        <td><input className="input-underline w-20 tabular" type="number" value={it.valor_venda || 0} onChange={(e) => setImportField(i, "valor_venda", e.target.value)} /></td>
                        <td><input className="input-underline w-16 tabular" type="number" value={it.quantidade_estoque || 0} onChange={(e) => setImportField(i, "quantidade_estoque", e.target.value)} /></td>
                        <td><button onClick={() => removeImportItem(i)} className="text-[#8B5E5E]" data-testid={`import-remove-${i}`}><Trash size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {importItems.length > 500 && <div className="mt-3 text-xs text-[#5C5A57]" data-testid="import-truncated">Exibindo os primeiros 500 de {importItems.length} itens. Todos serão importados ao confirmar.</div>}
              {(parseData.errors || []).length > 0 && (
                <div className="mt-4 p-3 bg-[#F4E6E5] border border-[#DDA7A5] text-xs text-[#7A2D2B]" data-testid="import-errors">
                  {parseData.errors.length} linha(s) com erro (sem nome/descrição) foram descartadas: {parseData.errors.slice(0, 5).map((e) => `linha ${e.linha}`).join(", ")}{parseData.errors.length > 5 ? "..." : ""}
                </div>
              )}
            </div>
          )}

          {/* Produtos que sumiram do arquivo */}
          {parseData && (parseData.missing_count || 0) > 0 && (
            <div className="border border-[#E4CF9E] bg-[#FBF9F0] p-6 mb-6" data-testid="missing-section">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex-1">
                  <h2 className="section-title text-[#7A5B1E]">Produtos que sumiram do arquivo ({parseData.missing_count})</h2>
                  <p className="text-xs text-[#5C5A57] mt-1">Ativos no catálogo cujo código de barras NÃO veio neste arquivo. Nada é alterado automaticamente — decida a ação abaixo.</p>
                </div>
                <select className="input-underline" value={missingAction} onChange={(e) => setMissingAction(e.target.value)} data-testid="missing-action-select">
                  <option value="manter">Manter ativo</option>
                  <option value="desativar">Desativar</option>
                  <option value="zerar_estoque">Zerar estoque</option>
                  <option value="ignorar">Ignorar</option>
                </select>
                <button onClick={applyMissingAction} disabled={applyingMissing} className="brand" data-testid="missing-apply-btn">{applyingMissing ? "Aplicando..." : `Aplicar (${missingSel.size})`}</button>
              </div>
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="crl w-full">
                  <thead><tr>
                    <th><input type="checkbox" checked={missingSel.size === (parseData.missing || []).length && (parseData.missing || []).length > 0} onChange={toggleAllMissing} data-testid="missing-select-all" /></th>
                    <th>Cód. interno</th><th>Nome</th><th>Código de barras</th><th>Estoque</th>
                  </tr></thead>
                  <tbody>
                    {(parseData.missing || []).map((m) => (
                      <tr key={m.id} data-testid={`missing-row-${m.id}`}>
                        <td><input type="checkbox" checked={missingSel.has(m.id)} onChange={() => toggleMissing(m.id)} data-testid={`missing-check-${m.id}`} /></td>
                        <td className="tabular text-xs text-[#8B5E5E]">{m.codigo_interno}</td>
                        <td className="text-sm">{m.nome_produto}</td>
                        <td className="text-xs text-[#5C5A57]">{m.codigo_barras}</td>
                        <td className="tabular">{m.quantidade_estoque}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(parseData.missing_count || 0) > (parseData.missing || []).length && (
                <div className="mt-2 text-xs text-[#5C5A57]">Exibindo {(parseData.missing || []).length} de {parseData.missing_count}.</div>
              )}
            </div>
          )}

          {/* Histórico de importações */}
          <div className="border border-[#EBE5DF] bg-white p-6" data-testid="import-history">
            <h2 className="section-title mb-4">Histórico de importações</h2>
            {history.length === 0 ? (
              <div className="text-sm text-[#5C5A57]" data-testid="history-empty">Nenhuma importação realizada ainda.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="crl w-full">
                  <thead><tr><th>Data</th><th>Arquivo</th><th>Origem</th><th>Criados</th><th>Atualizados</th><th>Ignorados</th><th>Usuário</th></tr></thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} data-testid={`history-row-${h.id}`}>
                        <td className="text-xs text-[#5C5A57]">{formatDateTime(h.at)}</td>
                        <td className="text-sm">{h.filename || "-"}</td>
                        <td className="text-[10px] tracking-widest uppercase">{h.source}</td>
                        <td className="tabular text-[#3F6B45]">{h.created}</td>
                        <td className="tabular text-[#2D6CB4]">{h.updated}</td>
                        <td className="tabular text-[#8B7A2E]">{h.ignorados}</td>
                        <td className="text-xs">{h.user_name || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MOVEMENTS */}
      {tab === "movements" && (
        <div className="border border-[#EBE5DF] bg-white overflow-x-auto">
          <table className="crl w-full">
            <thead><tr><th>Data</th><th>Produto</th><th>Tipo</th><th>Antes</th><th>Depois</th><th>Δ</th><th>Usuário</th><th>Motivo</th></tr></thead>
            <tbody>
              {movements.length === 0 && <tr><td colSpan={8} className="text-center text-[#5C5A57] py-6" data-testid="movements-empty">Nenhuma movimentação.</td></tr>}
              {movements.map((m) => (
                <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                  <td className="text-xs text-[#5C5A57]">{formatDateTime(m.at)}</td>
                  <td className="text-sm">{m.product_name}</td>
                  <td className="text-[10px] tracking-widest uppercase">{m.type}</td>
                  <td className="tabular">{m.qty_before}</td>
                  <td className="tabular font-medium">{m.qty_after}</td>
                  <td className={`tabular font-medium ${m.delta < 0 ? "text-[#B4564F]" : "text-[#3F6B45]"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</td>
                  <td className="text-xs">{m.user_name || "-"}</td>
                  <td className="text-xs text-[#5C5A57]">{m.reason || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && (
        <div className="max-w-xl space-y-6">
          <div className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-3">Estoque</h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!settings.block_on_insufficient} onChange={(e) => saveSettings({ block_on_insufficient: e.target.checked })} data-testid="setting-block-stock" />
              <span className="text-sm">Bloquear venda quando não houver estoque suficiente</span>
            </label>
            <p className="text-xs text-[#5C5A57] mt-2">{settings.block_on_insufficient ? "A venda será bloqueada se faltar estoque." : "Apenas alerta: a venda é permitida mesmo com estoque zerado."}</p>
          </div>

          <div className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-3">Alteração de preço na venda</h2>
            <select className="input-underline" value={settings.price_change_policy || "all"} onChange={(e) => saveSettings({ price_change_policy: e.target.value })} data-testid="setting-price-policy">
              <option value="all">Vendedor pode alterar preços</option>
              <option value="admin_only">Apenas o administrador pode alterar</option>
              <option value="none">Ninguém pode alterar (preço fixo do catálogo)</option>
            </select>
            <p className="text-xs text-[#5C5A57] mt-2">Toda alteração de preço em relação ao catálogo é registrada na auditoria (valor original, valor informado, usuário, data e motivo).</p>
          </div>
        </div>
      )}

      {/* PRODUCT MODAL */}
      {prodModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="product-modal">
          <div className="bg-white max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-2xl font-bold mb-4">{prodModal.id ? "Editar produto" : "Novo produto"}</h3>
            {prodModal.id && (
              <div className="mb-4 text-xs text-[#5C5A57]">Código interno (permanente): <span className="tabular font-medium text-[#8B5E5E]" data-testid="pm-internal-code">{prodModal.codigo_interno || "-"}</span></div>
            )}
            {!prodModal.id && (
              <div className="mb-4 text-xs text-[#5C5A57]" data-testid="pm-internal-code-hint">O código interno (ex.: P0030) será gerado automaticamente ao salvar.</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nome *</label>
                <input className="input-underline w-full" value={prodModal.nome_produto} onChange={(e) => setProdModal({ ...prodModal, nome_produto: e.target.value })} data-testid="pm-name" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Código de barras</label>
                <input className="input-underline w-full" value={prodModal.codigo_barras || ""} onChange={(e) => setProdModal({ ...prodModal, codigo_barras: e.target.value })} data-testid="pm-barcode" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Categoria</label>
                <select className="input-underline w-full" value={prodModal.categoria || ""} onChange={(e) => setProdModal({ ...prodModal, categoria: e.target.value })} data-testid="pm-category">
                  <option value="">-</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Custo (R$)</label>
                <BRLInput value={prodModal.custo} onChange={(v) => setProdModal({ ...prodModal, custo: v })} testid="pm-cost" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Valor de venda (R$)</label>
                <BRLInput value={prodModal.valor_venda} onChange={(v) => setProdModal({ ...prodModal, valor_venda: v })} testid="pm-price" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Estoque</label>
                <input className="input-underline w-full tabular" type="number" value={prodModal.quantidade_estoque} onChange={(e) => setProdModal({ ...prodModal, quantidade_estoque: e.target.value })} data-testid="pm-stock" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Status</label>
                <select className="input-underline w-full" value={prodModal.status} onChange={(e) => setProdModal({ ...prodModal, status: e.target.value })} data-testid="pm-status">
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setProdModal(null)} className="flex-1 text-xs tracking-widest uppercase font-medium border border-[#EBE5DF] py-3" data-testid="pm-cancel">Cancelar</button>
              <button onClick={saveProduct} className="brand flex-1" data-testid="pm-save">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* STOCK ADJUST MODAL */}
      {adjModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="adjust-modal">
          <div className="bg-white max-w-sm w-full p-6">
            <h3 className="font-display text-xl font-bold mb-1">Ajustar estoque</h3>
            <p className="text-sm text-[#5C5A57] mb-4">{adjModal.product.nome_produto} · atual {adjModal.product.quantidade_estoque}</p>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nova quantidade</label>
            <input className="input-underline w-full tabular mb-3" type="number" value={adjModal.quantidade} onChange={(e) => setAdjModal({ ...adjModal, quantidade: e.target.value })} data-testid="adj-qty" />
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Motivo</label>
            <input className="input-underline w-full mb-4" value={adjModal.reason} onChange={(e) => setAdjModal({ ...adjModal, reason: e.target.value })} placeholder="Ex: Contagem física" data-testid="adj-reason" />
            <div className="flex gap-3">
              <button onClick={() => setAdjModal(null)} className="flex-1 text-xs tracking-widest uppercase font-medium border border-[#EBE5DF] py-3" data-testid="adj-cancel">Cancelar</button>
              <button onClick={saveAdjust} className="brand flex-1" data-testid="adj-save">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
